import {
    entityConfigToType,
    MutableEntityType,
} from 'app/agriculture/mutations/types/entityConfig';
import { useQueryClient } from '@tanstack/react-query';
import { useEntityQueryKey } from 'app/agriculture/queries/queryKey/useEntityQueryKey';
import { useCallback } from 'react';

type EntityMap<T extends MutableEntityType> = Map<
    entityConfigToType[T]['id'],
    entityConfigToType[T]
>;

export function useEntityCacheInvalidation<T extends MutableEntityType>(entityType: T) {
    const queryClient = useQueryClient();
    const queryKey = useEntityQueryKey()(entityType);

    const addToCache = useCallback(
        (entity: entityConfigToType[T]) => {
            queryClient.setQueryData<EntityMap<T>>(queryKey, (entities) => {
                // if we have no cache then we have no need to manage the cache
                if (!entities) return;

                // we want to avoid mutating the entities map because otherwise react-query does not detect a change
                const copy = new Map(entities);
                copy.set(entity.id, entity);

                return copy;
            });
        },
        [queryKey, queryClient],
    );

    const removeFromCache = useCallback(
        (id: string | number) => {
            queryClient.setQueryData<EntityMap<T>>(queryKey, (entities) => {
                // if we have no cache then we have no need to manage the cache
                if (!entities) return;

                // if the entity we want to remove is not in the map, then return the same map
                if (!entities.get(id)) return entities;

                // we want to avoid mutating the entities map because otherwise react-query does not detect a change
                const copy = new Map(entities);
                copy.delete(id);

                return copy;
            });
        },
        [queryKey, queryClient],
    );

    const updateCache = useCallback(
        (entity: entityConfigToType[T]) => {
            queryClient.setQueryData<EntityMap<T>>(queryKey, (entities) => {
                // if we have no cache then we have no need to manage the cache
                if (!entities) return;

                // we want to avoid mutating the entities map because otherwise react-query does not detect a change
                const copy = new Map(entities);
                copy.set(entity.id, entity);

                return copy;
            });
        },
        [queryKey, queryClient],
    );

    return {
        addToCache,
        removeFromCache,
        updateCache,
    };
}
